import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

const mainNav = [
  { to: "/", label: "首页" },
  { to: "/hot", label: "今日热榜" },
  { to: "/about", label: "关于我们" },
  { to: "/contact", label: "联系我们" },
];

export default function SiteHeader() {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) {
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const keyword = query.trim();
  const results = keyword
    ? articles
        .filter(
          (item) =>
            item.title.includes(keyword) ||
            item.tags.some((t) => t.includes(keyword)) ||
            item.category.includes(keyword),
        )
        .slice(0, 5)
    : [];

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b border-background-200 transition-colors ${
        scrolled ? "bg-background-50/95 backdrop-blur" : "bg-background-50"
      }`}
    >
      <div className="flex h-16 items-center gap-6 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-black text-background-50">
            52
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-black tracking-tight text-foreground-950">
              吃瓜网
            </span>
            <span className="mt-0.5 text-[10px] font-label tracking-[0.2em] text-primary-600">
              CHIGUA · 52
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {mainNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div ref={boxRef} className="relative ml-auto hidden w-64 xl:block">
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="搜索事件、话题或标签"
            className="w-full rounded-full border border-background-300 bg-background-100 py-2 pl-9 pr-4 text-sm font-label text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50"
          />
          {keyword ? (
            <div className="absolute left-0 top-12 w-full overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1.5">
              {results.length ? (
                results.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="block px-3.5 py-2 text-sm font-label text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <span className="line-clamp-1">{item.title}</span>
                  </Link>
                ))
              ) : (
                <p className="px-3.5 py-2 text-sm font-label text-foreground-400">
                  没有匹配的事件
                </p>
              )}
            </div>
          ) : null}
        </div>

        <Link
          to="/contact"
          className="ml-auto hidden shrink-0 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 lg:ml-0 lg:block"
        >
          我要投稿
        </Link>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="打开导航菜单"
          className="ml-auto flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-800 lg:hidden"
        >
          <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
        </button>
      </div>

      <div className="hidden border-t border-background-200 bg-background-100/70 lg:block">
        <div className="flex items-center gap-1 px-8 py-2">
          <span className="mr-2 flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-primary-600">
            <i className="ri-fire-line animate-pulse-dot text-sm"></i>
            热门分类
          </span>
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className="whitespace-nowrap rounded-full px-3 py-1 text-xs font-label text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-950"
            >
              {cat.name}
            </Link>
          ))}
          <Link
            to="/hot"
            className="ml-auto flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-label font-medium text-accent-900 transition-colors hover:bg-accent-200"
          >
            <i className="ri-bar-chart-box-line text-sm"></i>
            实时热度榜
          </Link>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 lg:hidden">
          <div className="relative mb-4">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400"></i>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索事件、话题或标签"
              className="w-full rounded-full border border-background-300 bg-background-100 py-2.5 pl-9 pr-4 text-sm font-label text-foreground-900 outline-none focus:border-primary-400"
            />
          </div>
          {keyword && results.length ? (
            <div className="mb-4 overflow-hidden rounded-lg border border-background-200">
              {results.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="block border-b border-background-100 px-3.5 py-2.5 text-sm font-label text-foreground-700 last:border-0"
                >
                  {item.title}
                </Link>
              ))}
            </div>
          ) : null}
          <nav className="flex flex-col gap-1">
            {mainNav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2.5 text-sm font-label ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-800 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <div className="mt-2 flex flex-wrap gap-2 border-t border-background-200 pt-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-xs font-label text-foreground-600"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}